import { JsonLd } from "@/components/JsonLd";
import { LOGO_URL, ORG_ID, SITE_URL } from "@/lib/site";

interface ArticleStructuredDataProps {
  type?: "Article" | "TechArticle";
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  url: string;
  articleSection?: string[];
}

export const ArticleStructuredData = ({
  type = "Article",
  title,
  description,
  image,
  datePublished,
  dateModified,
  author,
  url,
  articleSection,
}: ArticleStructuredDataProps) => {
  const organizationAuthor = [
    "ai evolution polska",
    "ai evolution team",
    "ai evolution labs",
  ].includes(author.trim().toLocaleLowerCase("pl"));
  const structuredData = {
    "@context": "https://schema.org",
    "@type": type,
    // Google truncates headlines past ~110 characters.
    headline: title.length > 110 ? `${title.slice(0, 107).trimEnd()}…` : title,
    name: title,
    description: description,
    image: image
      ? [new URL(image, "https://www.aievolutionpolska.pl").href]
      : undefined,
    isAccessibleForFree: true,
    inLanguage: "pl-PL",
    // Nagłówki artykułu jako sekcje - Google używa ich do spisu treści w wynikach.
    ...(articleSection && articleSection.length > 0
      ? { articleSection: articleSection }
      : {}),
    datePublished: datePublished,
    dateModified: dateModified || datePublished,
    url: url,
    author: organizationAuthor
      ? {
          "@type": "Organization",
          "@id": ORG_ID,
          name: "AI Evolution Polska",
          url: `${SITE_URL}/`,
        }
      : { "@type": "Person", name: author, url: `${SITE_URL}/` },
    // Same @id as the organisation node in the root graph, so the two merge
    // instead of describing two different publishers.
    publisher: {
      "@type": "Organization",
      "@id": ORG_ID,
      name: "AI Evolution Polska",
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  return <JsonLd data={structuredData} />;
};

export const BreadcrumbStructuredData = ({
  items,
}: {
  items: Array<{ name: string; url: string }>;
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return <JsonLd data={structuredData} />;
};
